import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useSearchParams } from 'react-router';

import useDebounceCallback from 'hooks/useDebounceCallback';
import { itemQueuePaginatedQuery } from 'pages/disruption/api';
import { validateForDaysCalculated } from 'pages/disruption/queue/utils';
import type { DisruptionQueueParams, DisruptionSortBy, DisruptionSortFields } from 'pages/disruption/types';

const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_SORT: DisruptionSortBy = 'description|asc';
const SEARCH_MIN_LENGTH = 3;

export function useEventItems(eventId: string) {
  const [searchParams] = useSearchParams();
  const clinicalLocation = searchParams.get('for_clinical_location') || '';
  const clinicalLocationGroup = searchParams.get('for_clinical_location_group') || '';
  const forDaysCalculated = validateForDaysCalculated(searchParams.get('for_days_calculated'));

  const [pageNumber, setPageNumber] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
  const [sort, setSort] = React.useState<DisruptionSortBy>(DEFAULT_SORT);
  const [searchInput, setSearchInput] = React.useState('');
  const [committedSearch, setCommittedSearch] = React.useState('');

  const commitSearch = useDebounceCallback((raw: string) => {
    const trimmed = raw.trim();
    const nextSearch = trimmed.length >= SEARCH_MIN_LENGTH ? trimmed : '';
    setCommittedSearch(nextSearch);
    setPageNumber(1);
  }, 500);

  React.useEffect(() => {
    setPageNumber(1);
  }, [clinicalLocation, clinicalLocationGroup, forDaysCalculated]);

  const params = React.useMemo((): DisruptionQueueParams => {
    return {
      for_event: [eventId],
      for_days_calculated: forDaysCalculated,
      page_number: pageNumber,
      page_size: pageSize,
      include_doh_rollup: true,
      include_on_order_qty_rollup: true,
      sort,
      ...(committedSearch ? { for_search_string: committedSearch } : {}),
      ...(clinicalLocation ? { for_clinical_location: clinicalLocation } : {}),
      ...(clinicalLocationGroup ? { for_clinical_location_group: clinicalLocationGroup } : {}),
    };
  }, [
    clinicalLocation,
    clinicalLocationGroup,
    committedSearch,
    eventId,
    forDaysCalculated,
    pageNumber,
    pageSize,
    sort,
  ]);

  const query = useQuery({
    ...itemQueuePaginatedQuery(params),
  });

  const handleSearchChange = React.useCallback(
    (value: string) => {
      setSearchInput(value);
      commitSearch(value);
    },
    [commitSearch]
  );

  const handleSortChange = React.useCallback((field: DisruptionSortFields) => {
    setSort((current) => {
      const [currentField, currentDir] = current.split('|');
      const nextDir = currentField === field && currentDir === 'asc' ? 'desc' : 'asc';
      return `${field}|${nextDir}` as DisruptionSortBy;
    });
    setPageNumber(1);
  }, []);

  const handlePageSizeChange = React.useCallback((nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPageNumber(1);
  }, []);

  return {
    items: query.data?.items ?? [],
    resultSetSize: query.data?.result_set_size ?? 0,
    dohRollup: query.data?.doh_rollup,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    pageNumber,
    pageSize,
    sort,
    searchInput,
    hasActiveSearch: Boolean(committedSearch),
    hasItemListFilter: Boolean(committedSearch || clinicalLocation || clinicalLocationGroup),
    setSearchInput: handleSearchChange,
    setPageNumber,
    setPageSize: handlePageSizeChange,
    setSort: handleSortChange,
  };
}
